import DefaultLayout from "../../layout/DefaultLayout";
import BreadcrumbTwo from "../../components/Breadcrumbs/BreadcrumbTwo";
import { Lock, Database, Shield, UserCheck, Mail, Cloud } from "lucide-react";

/**
 * 🔒 Página “Política de Privacidad” — Versión extendida
 */
const AboutPrivacy = () => {
  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      {/* 🧭 Contenedor principal */}
      <div className="flex flex-col gap-7.5">
        {/* 📘 Card principal */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          {/* 🔹 Encabezado */}
          <div className="border-b border-stroke px-4 py-6 dark:border-strokedark sm:px-6 xl:px-9">
            <h3 className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Política de Privacidad
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-3xl leading-relaxed">
              En <strong>Codenova POS</strong>, tu privacidad y la protección de tus
              datos personales son una prioridad. Esta política explica cómo
              recopilamos, utilizamos, almacenamos y protegemos tu información al
              usar nuestros servicios, de acuerdo con la normativa vigente y las
              mejores prácticas de seguridad.
            </p>
          </div>

          {/* 🔸 Contenido */}
          <div className="flex flex-col gap-10 p-4 sm:p-6 xl:p-9">
            {/* 1️⃣ Recolección de Información */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Database className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  1. Recolección de Información
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Recopilamos únicamente la información necesaria para la operación
                  del sistema, incluyendo datos de empresa, usuarios, clientes,
                  documentos tributarios y parámetros de configuración. Esta
                  información nos permite garantizar la funcionalidad, integridad y
                  cumplimiento fiscal de cada cuenta.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Adicionalmente, podemos recopilar datos técnicos como dirección IP,
                  navegador utilizado, sistema operativo y actividad dentro de la
                  plataforma, con el fin de mejorar la seguridad y el rendimiento del
                  sistema.
                </p>
              </div>
            </div>

            {/* 2️⃣ Uso de los Datos */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Shield className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  2. Uso de los Datos
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Los datos proporcionados se utilizan exclusivamente con fines
                  operativos, administrativos, fiscales y de soporte técnico dentro
                  del ecosistema de <strong>Codenova POS</strong>. En ningún caso
                  compartimos, vendemos ni transferimos tus datos personales a
                  terceros sin tu consentimiento expreso.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Solo se podrá compartir información con entidades reguladoras o
                  autoridades fiscales cuando sea legalmente requerido, conforme a la
                  legislación salvadoreña y estándares internacionales de protección
                  de datos.
                </p>
              </div>
            </div>

            {/* 3️⃣ Almacenamiento y Seguridad */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Cloud className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  3. Almacenamiento y Seguridad de la Información
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Toda la información se aloja en servidores seguros en la nube de{" "}
                  <strong>AWS (Amazon Web Services)</strong>, utilizando tecnologías de
                  cifrado y protocolos avanzados de seguridad. Aplicamos medidas
                  técnicas y administrativas para prevenir accesos no autorizados,
                  pérdida de datos o uso indebido de la información.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Mantenemos copias de seguridad automáticas y políticas de
                  recuperación ante desastres para garantizar la continuidad de la
                  operación y la protección de los datos empresariales.
                </p>
              </div>
            </div>

            {/* 4️⃣ Derechos del Usuario */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <UserCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  4. Derechos del Usuario
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Los usuarios tienen derecho a solicitar en cualquier momento el
                  acceso, rectificación, actualización o eliminación de sus datos
                  personales. Estas solicitudes serán atendidas dentro de los plazos
                  razonables y conforme a las disposiciones legales aplicables.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  <strong>Codenova POS</strong> garantiza la confidencialidad de la
                  información personal y el derecho a decidir sobre el uso de tus
                  datos, siempre bajo un enfoque de transparencia y respeto.
                </p>
              </div>
            </div>

            {/* 5️⃣ Contacto y Soporte */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  5. Contacto y Soporte
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Si tienes dudas, solicitudes o comentarios sobre esta Política de
                  Privacidad, puedes comunicarte con nuestro equipo de soporte a
                  través de los siguientes medios oficiales:
                </p>
                <ul className="mt-3 flex flex-col gap-2 text-sm">
                  <li className="text-primary underline">
                    codenovasoportesv@gmail.com
                  </li>
                </ul>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  El horario de atención es de <strong>lunes a viernes de 8:00 a.m. a
                  5:00 p.m.</strong> (hora de El Salvador). Nuestro equipo estará
                  disponible para atender tus consultas de forma personalizada.
                </p>
              </div>
            </div>

            {/* 🧩 Nota final */}
            <div className="mt-4 border-t border-stroke pt-6 dark:border-strokedark">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                Esta política puede ser actualizada en cualquier momento para
                reflejar cambios en nuestras prácticas o requerimientos legales.
                <br />
                El uso continuo de <strong>Codenova POS</strong> implica la aceptación
                de la versión más reciente de esta política. <br />
                Última actualización: <strong>octubre 2025</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AboutPrivacy;
